import React, { useState, useMemo, useCallback } from 'react'
import { Table, Tag, Button, Space, Popconfirm, message, Badge, Progress } from 'antd'
import { StopOutlined, CheckOutlined } from '@ant-design/icons'
import { isAxiosError } from 'axios'
import { humanAdminApi, HumanResponse } from '../../shared/api/humanApi'
import { useI18n } from '@/shared/i18n'

interface HumanListProps {
  humans: HumanResponse[]
  load: Record<number, number>
  onRefresh: () => void
}

const statusColor: Record<string, 'success' | 'default' | 'warning' | 'error' | 'processing'> = {
  online: 'success',
  offline: 'default',
  busy: 'warning',
}

const HumanList: React.FC<HumanListProps> = ({ humans, load, onRefresh }) => {
  const [processing, setProcessing] = useState<Set<number>>(new Set())
  const { t } = useI18n('modules')

  const statusLabel: Record<string, string> = {
    online: t('humanList.status.online'),
    offline: t('humanList.status.offline'),
    busy: t('humanList.status.busy'),
  }

  const toggleActive = useCallback(async (human: HumanResponse) => {
    setProcessing(prev => new Set([...prev, human.id]))
    try {
      await humanAdminApi.update(human.id, { is_active: !human.is_active })
      message.success(human.is_active ? t('humanList.toggleSuccess.disabled') : t('humanList.toggleSuccess.enabled'))
      onRefresh()
    } catch (e) {
      if (isAxiosError(e)) {
        message.error(e.response?.data?.detail || t('humanList.toggleFailed'))
      } else {
        message.error(t('humanList.toggleFailed'))
      }
    } finally {
      setProcessing(prev => { const s = new Set(prev); s.delete(human.id); return s })
    }
  }, [onRefresh, t])

  const columns = useMemo(() => [
    {
      title: t('humanList.col.displayName'),
      dataIndex: 'display_name' as const,
      key: 'display_name',
    },
    {
      title: t('humanList.col.username'),
      dataIndex: 'username' as const,
      key: 'username',
      render: (v: string) => <code>{v}</code>,
    },
    {
      title: t('humanList.col.status'),
      dataIndex: 'status' as const,
      key: 'status',
      render: (s: string) => (
        <Badge status={statusColor[s] ?? 'default'} text={statusLabel[s] ?? s} />
      ),
    },
    {
      title: t('humanList.col.load'),
      key: 'load',
      render: (_: unknown, r: HumanResponse) => {
        const current = load[r.id] ?? 0
        const pct = r.max_sessions > 0 ? Math.round((current / r.max_sessions) * 100) : 0
        return (
          <Space>
            <Progress percent={pct} size="small" style={{ width: 80 }} showInfo={false} />
            <span>{current}/{r.max_sessions}</span>
          </Space>
        )
      },
    },
    {
      title: t('humanList.col.isActive'),
      dataIndex: 'is_active' as const,
      key: 'is_active',
      render: (v: boolean) => (
        <Tag color={v ? 'success' : 'error'}>{v ? t('humanList.active.enabled') : t('humanList.active.disabled')}</Tag>
      ),
    },
    {
      title: t('humanList.col.action'),
      key: 'action',
      render: (_: unknown, r: HumanResponse) => (
        <Popconfirm
          title={r.is_active ? t('humanList.toggleConfirm.disable') : t('humanList.toggleConfirm.enable')}
          onConfirm={() => toggleActive(r)}
          okText={t('humanList.active.enabled')}
          cancelText={t('userManagement.createModal.cancel')}
        >
          <Button
            type="link"
            icon={r.is_active ? <StopOutlined /> : <CheckOutlined />}
            loading={processing.has(r.id)}
            danger={r.is_active}
          >
            {r.is_active ? t('humanList.active.disabled') : t('humanList.active.enabled')}
          </Button>
        </Popconfirm>
      ),
    },
  ], [processing, toggleActive, t])

  if (humans.length === 0) {
    return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>{t('humanList.empty')}</div>
  }

  return (
    <Table
      columns={columns}
      dataSource={humans}
      rowKey="id"
      pagination={false}
    />
  )
}

export default HumanList
